import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { MemoryRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { I18nProvider, useI18n } from './i18n';
import { playLinksAPI } from './services/api';
import { initPortalSdk, portalSdk, portalStorage, PORTAL } from './portal';
import { track, installTracking } from './utils/track';

const Game = lazy(() => import('./pages/FootballBossGame'));

const Screen = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center', justifyContent: 'center', background: '#0A0F0E', color: '#b8ff3d', fontFamily: 'system-ui, sans-serif', textAlign: 'center', padding: 24 }}>
    {children}
  </div>
);

// The portal build has no website: it boots straight into the game. A first-time player gets a
// guest club (no signup) and can save it later from inside the game.
function PortalBoot() {
  const { lang } = useI18n();
  const [state, setState] = useState(() => (localStorage.getItem('token') ? 'ready' : 'loading'));

  const start = useCallback(async () => {
    setState('loading');
    try {
      const res = await playLinksAPI.startGuest({ lang });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      portalStorage.set('mfg_token', res.data.token);
      portalStorage.set('mfg_user', JSON.stringify(res.data.user));
      track('guest_created', { portal: PORTAL });
      setState('ready');
    } catch (e) {
      track('portal_guest_error', { portal: PORTAL, status: e.response?.status || 0 });
      setState('error');
    }
  }, [lang]);

  // Visit event, JS error capture and flushing the queue when the tab is hidden or closed.
  useEffect(() => { installTracking(); }, []);

  useEffect(() => {
    initPortalSdk().then(() => {
      portalSdk.loadingStart();
      if (localStorage.getItem('token')) return;
      // A CrazyGames player coming back on another device: restore their club from the Data Module.
      const saved = portalStorage.get('mfg_token');
      if (saved) {
        localStorage.setItem('token', saved);
        localStorage.setItem('user', portalStorage.get('mfg_user') || 'null');
        setState('ready');
        return;
      }
      start();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (state !== 'ready') return;
    initPortalSdk().then(() => {
      // Keep the Data Module in sync so the club is never tied to just this browser.
      const token = localStorage.getItem('token');
      if (token && portalStorage.get('mfg_token') !== token) {
        portalStorage.set('mfg_token', token);
        portalStorage.set('mfg_user', localStorage.getItem('user') || 'null');
      }
      portalSdk.loadingStop();
      portalSdk.gameplayStart();
    });
    track('portal_game_ready', { portal: PORTAL });
  }, [state]);

  if (state === 'error') {
    return (
      <Screen>
        <div style={{ color: '#e2e8f0' }}>{lang === 'pt' ? 'Não foi possível criar o teu clube.' : "We couldn't create your club."}</div>
        <button onClick={start} style={{ background: '#b8ff3d', color: '#0A0F0E', border: 0, borderRadius: 8, padding: '10px 20px', fontWeight: 700, cursor: 'pointer' }}>
          {lang === 'pt' ? 'Tentar de novo' : 'Try again'}
        </button>
      </Screen>
    );
  }
  if (state !== 'ready') return <Screen>{lang === 'pt' ? 'A preparar o teu clube…' : 'Setting up your club…'}</Screen>;

  return (
    <Suspense fallback={<Screen>Loading…</Screen>}>
      <Game />
    </Suspense>
  );
}

export default function PortalApp() {
  return (
    <HelmetProvider>
      <I18nProvider>
        {/* MemoryRouter: the portal serves us from its own path, so URLs are not ours to change. */}
        <MemoryRouter initialEntries={['/game']}>
          <Routes>
            <Route path="/game" element={<PortalBoot />} />
            <Route path="*" element={<Navigate to="/game" replace />} />
          </Routes>
        </MemoryRouter>
      </I18nProvider>
    </HelmetProvider>
  );
}
